<template>
  <form v-if="loaded">
    <div>
      <vue-form-generator :model="form" :options="formOptions" :schema="schema" @validated="setValidState"></vue-form-generator>
    </div>
    <button id="save" data-test="save" class="btn btn-primary btn-block" :disabled="!isFormValid" @click.prevent="submit">
      {{ $t('network.user.save') }}
    </button>
    <div class="row">
      <div v-if="saving" class="text-center">
        <lottie-player
          autoplay
          loop
	        speed="2.5"
          src="/img/animations/custom_loader.json"
          style="height: 80px;"
          >
        </lottie-player>
        <p>{{ $t('saving') }}</p>
      </div>
      <div v-if="saved">
        <div class="alert alert-success alert-dismissible mt-4 mb-0" role="alert">
          <i class="fas fa-check"></i>
          {{ $t('ResearchCreds.your-information-was-saved') }}
          <button id="close" data-test="close" type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
      </div>
    </div>
  </form>
  <div v-else class="row text-center mt-5">
    <lottie-player
      autoplay
      loop
	    speed="2.5"
      src="/img/animations/custom_loader.json"
      style="height: 130px;"
      >
    </lottie-player>
  </div>
</template>
<script>
import '@/lib/vue-form-generator';
import { baseName } from '@/lib/util.js';

export default {
  data() {
    return {
      loaded: false,
      saving: false,
      saved: false,
      otherCategoryID: 0,
      categories: [],
      form: {},
      schema: {},
      formOptions: {
        validateAfterLoad: true,
        validateAfterChanged: true,
        validateAsync: true,
      },
      isFormValid: false,
    };
  },
  mounted() {
    this.getResearchCredsProfile();
  },
  methods: {
    setValidState(isFormValid) {
      this.isFormValid = isFormValid;
    },
    submit(event) {
      if (!this.isFormValid) {
        event.preventDefault();
      } else {
        this.save().then(() => this.$emit('submitted'));
      }
    },
    save() {
      // submit the information to the server and create the site
      this.saving = true;
      this.form.hsp_certificate_filename = baseName(this.form.hsp_certificate_file);
      this.form.gcp_certificate_filename = baseName(this.form.gcp_certificate_file);
      this.form.hipaa_certificate_filename = baseName(this.form.hipaa_certificate_file);
      this.form.other_certificate_filename = baseName(this.form.other_certificate_file);

      return this.axios.post('profile/researchcreds', { form: this.form }).then(() => {
        this.saving = false;
        this.saved = true;
        this.$store.dispatch('resetUserResearchCreds', this.form);
        this.$bus.emit('profile-progress-submit');
      });
    },
    getResearchCredsProfile() {
      this.$http.get('profile/researchcreds').then(response => {
        this.form = response.data.model;
        let displayedFields = ['professional_experience_years', 'clinical_experience_years', 'num_clinical_studies_as_principal_investigator', 'num_clinical_studies_as_sub_investigator'];
        this.form.hsp_certificate_filename = baseName(response.data.model.hsp_certificate_file);
        this.form.gcp_certificate_filename = baseName(response.data.model.gcp_certificate_file);
        this.form.hipaa_certificate_filename = baseName(response.data.model.hipaa_certificate_file);
        this.form.other_certificate_filename = baseName(response.data.model.other_certificate_file);
        this.categories = response.data.categories;
        this.otherCategoryID = response.data.otherCategoryID;
        this.buildSchema();
        this.loaded = true;
        this.$nextTick(() => {
          for (let key in this.form) {
            if ((this.form[key] == null || this.form[key] === '') && displayedFields.includes(key)) {
              this.addBorder(key);
            }
          }
        });
      });
    },
    validateInteger(value, data) {
      if (value == null) {
        return;
      }
      let msgMax = this.$t('the-number-is-too-big-maximum');
      value = value.toString(); // Convert value to a string
      if (value < 0) {
        return [this.$t('the-number-is-too-small-minimum-0')];
      } else if (value > data.max) {
        return [msgMax+data.max];
      } else if (data.max == 99 && !value.match(/^[1-9]{0,1}[0-9]$/g) && value != '') {
        return [this.$t('please-use-whole-numbers-only')];
      } else if (data.max == 1000 && !value.match(/^([0-9]|[1-9][0-9]|[1-9][0-9][0-9]|1000)$/g) && value != '') {
        return [this.$t('please-use-whole-numbers-only')];
      }
    },
    getElementId(value) {
      const elementIds = {
        'professional_experience_years': 'professional-experience',
        'clinical_experience_years': 'clinical-research-experience',
        'num_clinical_studies_as_principal_investigator': 'as-a-principal-investigator',
        'num_clinical_studies_as_sub_investigator': 'as-a-sub-investigator',
      };
      return elementIds[value] || null;
    },
    addBorder(value) {
      const elementId = this.getElementId(value);
      const element = document.getElementById(elementId);
      if (element) {
        element.classList.add('border-2', 'border-info');
      }
    },
    removeBorder(value) {
      const elementId = this.getElementId(value);
      const element = document.getElementById(elementId);
      if (element) {
        element.classList.remove('border-2', 'border-info');
      }
    },
    buildSchema() {
      this.schema = {
        groups: [
          {
            legend: this.$t('auth.researchcreds.experience'),
            fields: [
              {
                type: 'input',
                inputType: 'text',
                label: this.$t('Investigator.professional-experience-years'),
                placeholder: this.$t('enter-number-of-years-of-experience'),
                model: 'professional_experience_years',
                min: 0,
                max: 99,
                required: false,
                validator: this.validateInteger,
                styleClasses:'column pe-3',
                onChanged: (model, newVal) => {
                  if (newVal === null || newVal === undefined || newVal === '') {
                    this.addBorder('professional_experience_years');
                  } else {
                    this.removeBorder('professional_experience_years');
                  }
                },
              },
              {
                type: 'input',
                inputType: 'text',
                label: this.$t('Investigator.clinical-research-experience-years'),
                placeholder: this.$t('enter-number-of-years-of-experience'),
                model: 'clinical_experience_years',
                min: 0,
                max: 99,
                required: false,
                validator: this.validateInteger,
                styleClasses:'column ps-3',
                onChanged: (model, newVal) => {
                  if (newVal === null || newVal === undefined || newVal === '') {
                    this.addBorder('clinical_experience_years');
                  } else {
                    this.removeBorder('clinical_experience_years');
                  }
                },
              },
            ],
          },
          {
            legend: this.$t('Investigator.how-many-clinical-research-studies-are-you-currently-involved-in'),
            fields: [
              {
                type: 'input',
                inputType: 'text',
                label: this.$t('Investigator.as-a-principal-investigator'),
                placeholder: this.$t('enter-number-of-studies'),
                model: 'num_clinical_studies_as_principal_investigator',
                min: 0,
                max: 1000,
                required: false,
                validator: this.validateInteger,
                styleClasses:'column pe-3',
                onChanged: (model, newVal) => {
                  if (newVal === null || newVal === undefined || newVal === '') {
                    this.addBorder('num_clinical_studies_as_principal_investigator');
                  } else {
                    this.removeBorder('num_clinical_studies_as_principal_investigator');
                  }
                },
              },
              {
                type: 'input',
                inputType: 'text',
                label: this.$t('Investigator.as-a-sub-investigator'),
                placeholder: this.$t('enter-number-of-studies'),
                model: 'num_clinical_studies_as_sub_investigator',
                min: 0,
                max: 1000,
                required: false,
                validator: this.validateInteger,
                styleClasses:'column ps-3',
                onChanged: (model, newVal) => {
                  if (newVal === null || newVal === undefined || newVal === '') {
                    this.addBorder('num_clinical_studies_as_sub_investigator');
                  } else {
                    this.removeBorder('num_clinical_studies_as_sub_investigator');
                  }
                },
              },
            ],
          },
        ],
      };
    },
  },
};
</script>
<style>
.errors.help-block {
  color: red !important;
  font-size: small !important;
}
</style>